import {
    type MouseEvent,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import { DrawState, Simulation } from "../../pkg";

interface SimulationCanvasProps {
    simulation: Simulation;
}

export function SimulationCanvas(props: SimulationCanvasProps) {
    const { simulation } = props;

    const [simulationWidth, simulationHeight] = useMemo(() => {
        return [simulation.width, simulation.height];
    }, [simulation]);

    const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null);
    const drawState = useMemo(() => {
        if (canvas === null) {
            return null;
        }

        const ctx = canvas.getContext("webgl2");
        if (ctx === null) {
            throw new Error("Could not initialize WebGL2 context");
        }
        return new DrawState(ctx);
    }, [canvas]);

    useEffect(() => {
        if (drawState === null) {
            return;
        }

        const draw = () => {
            simulation.draw(drawState);
            id = window.requestAnimationFrame(draw);
        };
        let id = window.requestAnimationFrame(draw);

        return () => {
            window.cancelAnimationFrame(id);
        };
    }, [drawState, simulation]);

    const handleClick = useCallback(
        (e: MouseEvent<HTMLCanvasElement>) => {
            e.preventDefault();

            const canvas = e.currentTarget;

            const clientRect = canvas.getBoundingClientRect();
            const clickPos: [x: number, y: number] = [
                ((e.clientX - clientRect.x) / clientRect.width) *
                    simulationWidth,
                ((e.clientY - clientRect.y) / clientRect.height) *
                    simulationHeight,
            ];

            simulation.tap(clickPos);
        },
        [simulation, simulationWidth, simulationHeight],
    );

    return (
        <canvas
            ref={setCanvas}
            width={simulationWidth * 6}
            height={simulationHeight * 6}
            onClick={handleClick}
        />
    );
}
